/* ===========================================================
   templates.js — starter docs and the two welcome examples

   Real, useful starting points (no lorem ipsum). The welcome example
   for each mode shows off what that mode is for, and doubles as a
   quick guide.
   =========================================================== */

import { newSandbox } from './editor/nodes/sandbox.js';

const t = (text, marks) => ({ type: 'text', text, ...(marks ? { marks: marks.map(m => (typeof m === 'string' ? { type: m } : m)) } : {}) });
const p = (...kids) => ({ type: 'paragraph', content: kids.filter(k => k !== '').map(k => (typeof k === 'string' ? t(k) : k)) });
const h = (level, text) => ({ type: 'heading', attrs: { level }, content: [t(text)] });
const ul = (...items) => ({ type: 'bulletList', content: items.map(i => ({ type: 'listItem', content: [p(i)] })) });
const ol = (...items) => ({ type: 'orderedList', content: items.map(i => ({ type: 'listItem', content: [p(i)] })) });
const tasks = (...items) => ({ type: 'taskList', content: items.map(([done, text]) => ({ type: 'taskItem', attrs: { checked: done }, content: [p(text)] })) });
const quote = text => ({ type: 'blockquote', content: [p(text)] });
const math = latex => ({ type: 'mathBlock', attrs: { latex } });
const imath = latex => ({ type: 'mathInline', attrs: { latex } });
const code = (language, text) => ({ type: 'codeBlock', attrs: { language }, content: [t(text)] });
const cell = (text, header) => ({ type: header ? 'tableHeader' : 'tableCell', content: [p(text)] });
const table = rows => ({ type: 'table', content: rows.map((r, i) => ({ type: 'tableRow', content: r.map(c => cell(c, i === 0)) })) });
const doc = (...content) => ({ type: 'doc', content });

export const WELCOME_CASUAL = {
  title: 'Welcome to Docs Protocol',
  json: doc(
    p('This is a doc. Click anywhere and start typing. Everything saves by itself, even with no internet.'),
    h(2, 'Make it look nice'),
    p('Use the bar at the top for ', t('bold', ['bold']), ', ', t('italic', ['italic']), ', ', t('highlights', ['highlight']), ', lists and headings. Shortcuts work too: ', t('Ctrl B', ['code']), ' for bold, ', t('Ctrl I', ['code']), ' for italic.'),
    p('Typing shortcuts quietly work as well: start a line with ', t('#', ['code']), ' and a space for a heading, or ', t('-', ['code']), ' and a space for a list.'),
    h(2, 'Checklists'),
    tasks([true, 'Open my first doc'], [false, 'Try the checklist button'], [false, 'Share a doc with a friend']),
    h(2, 'Tables'),
    table([['Subject', 'Due', 'Done?'], ['Science report', 'Friday', 'Not yet'], ['Math worksheet', 'Monday', 'Yes']]),
    h(2, 'Equations'),
    p('Insert → Equation lets you build one with a live preview. Like the area of a circle, ', imath('A = \\pi r^2'), ', or the quadratic formula:'),
    math('x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}'),
    h(2, 'Never lose a draft'),
    p('Open ', t('Version history', ['bold']), ' (the clock button) to save a version, go back to an older one, or ', t('try an edit on a copy', ['bold']), ' and merge it back when you like it.'),
    quote('Tip: press Ctrl K (or tap the magnifying glass) to find any doc or action.'),
  ),
};

export const WELCOME_DEV = {
  title: 'Welcome, developer',
  json: doc(
    p('Same friendly editor, plus the tools you\'d expect. Toggle ', t('Markdown source', ['bold']), ' from the toolbar to edit this doc as raw Markdown.'),
    h(2, 'Math, typed directly'),
    p('Inline: type ', t('$e^{i\\pi} + 1 = 0$', ['code']), ' and it becomes ', imath('e^{i\\pi} + 1 = 0'), '. Block: start a line with ', t('$$', ['code']), ' and a space.'),
    math('\\sum_{k=1}^{n} k = \\frac{n(n+1)}{2}'),
    h(2, 'Runnable code'),
    p('Python, JavaScript and SQL run in your browser (never on our server). Press Run.'),
    code('python', 'import statistics\nscores = [72, 88, 95, 61, 79]\nprint("mean:", statistics.mean(scores))\nprint("stdev:", round(statistics.stdev(scores), 2))'),
    code('javascript', 'const fib = n => (n < 2 ? n : fib(n - 1) + fib(n - 2));\nconsole.log([...Array(12).keys()].map(fib).join(", "));'),
    code('sql', "CREATE TABLE notes (id INTEGER PRIMARY KEY, title TEXT, words INT);\nINSERT INTO notes (title, words) VALUES ('Plan', 120), ('Draft', 940), ('Ideas', 310);\nSELECT title, words FROM notes ORDER BY words DESC;"),
    h(2, 'Live sandbox'),
    p('HTML, CSS and JS that render as you type, with console output underneath. It runs in a locked-down frame that can\'t touch your account.'),
    newSandbox(),
    h(2, 'History, the Git way'),
    ul('Commit: save a named version (clock button → Commit)', 'Branch: try an edit on a copy, then diff and merge it back', 'Conflicts get a side-by-side picker'),
  ),
};

export const TEMPLATES = [
  { id: 'blank', name: 'Blank', icon: 'doc', json: doc(p('')) },
  {
    id: 'notes', name: 'Notes', icon: 'pencil',
    json: doc(h(1, 'Notes'), p(t('Date: ', ['bold']), new Date().toLocaleDateString()), h(2, 'Key points'), ul('', ''), h(2, 'Questions'), ul(''), h(2, 'To do'), tasks([false, ''])),
  },
  {
    id: 'todo', name: 'To-do list', icon: 'checklist',
    json: doc(h(1, 'To do'), h(3, 'Today'), tasks([false, ''], [false, '']), h(3, 'This week'), tasks([false, ''])),
  },
  {
    id: 'report', name: 'School report', icon: 'quote',
    json: doc(h(1, 'Report title'), p(t('Name · Class · Date', ['italic'])), h(2, 'Introduction'), p(''), h(2, 'Main points'), p(''), h(2, 'Conclusion'), p(''), h(2, 'Sources'), ol('')),
  },
  {
    id: 'plan', name: 'Plan', icon: 'table',
    json: doc(h(1, 'Plan'), p(t('Goal: ', ['bold'])), h(2, 'Steps'), table([['Step', 'Who', 'When'], ['', '', ''], ['', '', '']]), h(2, 'Notes'), p('')),
  },
  {
    id: 'tech', name: 'Tech doc', icon: 'code', dev: true,
    json: doc(h(1, 'Design: '), p(t('Status: ', ['bold']), 'draft'), h(2, 'Context'), p(''), h(2, 'Proposal'), p(''), h(2, 'Example'), code('javascript', '// '), h(2, 'Open questions'), tasks([false, ''])),
  },
  {
    id: 'notebook', name: 'Code notebook', icon: 'play', dev: true,
    json: doc(h(1, 'Notebook'), p('Python state carries over between blocks, like a notebook.'), code('python', 'data = [3, 1, 4, 1, 5, 9, 2, 6]\nprint(sorted(data))'), code('python', 'print(sum(data) / len(data))')),
  },
];
